import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { LoyaltyBadge } from "@/components/app/LoyaltyBadge";
import { StatusBadge } from "@/components/app/StatusBadge";
import SubaccountsDialog from "@/components/app/SubaccountsDialog";
import { formatBRL, formatDate } from "@/lib/format";
import { Plus, MagnifyingGlass, DotsThreeVertical, PencilSimple, Trash, CurrencyDollar, Lock, LockOpen, ArrowClockwise, ClipboardText, Lightning, ClockCounterClockwise, Users } from "@phosphor-icons/react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all",     label: "Todos" },
  { key: "em_dia",  label: "Em dia" },
  { key: "proximo", label: "Próximos" },
  { key: "vencido", label: "Vencidos" },
  { key: "inativo", label: "Inativos" },
];

const emptyForm = {
  name: "", email: "", username: "", password: "", phone: "",
  plan_id: "", plan_value: "", recurrence: "mensal", due_day: 10,
  current_due_date: "",
  discount_pct: 0, custom_loyalty: "", notes: "",
};

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [plans, setPlans] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [openPay, setOpenPay] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [generated, setGenerated] = useState(null);
  const [auditOpen, setAuditOpen] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [subsOpen, setSubsOpen] = useState(null); // client whose subaccounts are being managed

  const showAudit = async (client) => {
    setAuditOpen(client);
    setAuditLoading(true); setAuditLogs([]);
    try {
      const { data } = await api.get(`/admin/clients/${client.id}/audit-logs`);
      setAuditLogs(data);
    } catch { toast.error("Erro ao carregar histórico"); }
    finally { setAuditLoading(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/clients", { params: filter !== "all" ? { filter } : {} });
      setClients(data);
    } catch { toast.error("Erro ao carregar clientes"); }
  };
  const loadPlans = async () => {
    try { const { data } = await api.get("/admin/plans"); setPlans(data); } catch {}
  };

  useEffect(() => { load(); }, [filter]);
  useEffect(() => { loadPlans(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return clients;
    return clients.filter((c) =>
      [c.name, c.email, c.username, c.phone].filter(Boolean).some((f) => f.toLowerCase().includes(s))
    );
  }, [clients, q]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpenForm(true); setGenerated(null); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", email: c.email || "", username: c.username || "", password: "",
      phone: c.phone || "", plan_id: c.plan_id || "", plan_value: c.plan_value || "",
      recurrence: c.recurrence || "mensal", due_day: c.due_day || 10,
      current_due_date: c.current_due_date || "",
      discount_pct: c.discount_pct || 0, custom_loyalty: c.custom_loyalty || "",
      notes: c.notes || "",
    });
    setOpenForm(true); setGenerated(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (payload.plan_value === "") delete payload.plan_value; else payload.plan_value = Number(payload.plan_value);
    payload.due_day = Number(payload.due_day);
    payload.discount_pct = Number(payload.discount_pct);
    if (!payload.custom_loyalty) delete payload.custom_loyalty;
    if (!payload.password) delete payload.password;
    if (!payload.plan_id) delete payload.plan_id;
    if (!payload.current_due_date) delete payload.current_due_date;

    try {
      if (editing) {
        await api.put(`/admin/clients/${editing.id}`, payload);
        toast.success("Cliente atualizado");
        setOpenForm(false);
      } else {
        const { data } = await api.post("/admin/clients", payload);
        toast.success("Cliente cadastrado");
        if (data.generated_password) {
          setGenerated({ username: data.client.username, password: data.generated_password });
        } else {
          setOpenForm(false);
        }
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const setStatus = async (c, status) => {
    try { await api.put(`/admin/clients/${c.id}`, { status }); toast.success("Status atualizado"); load(); }
    catch { toast.error("Erro"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir ${c.name}? Esta ação remove todo o histórico.`)) return;
    try { await api.delete(`/admin/clients/${c.id}`); toast.success("Cliente removido"); load(); }
    catch { toast.error("Erro"); }
  };

  const registerPay = async () => {
    if (!openPay) return;
    const amount = Number(payAmount || openPay.plan_value || 0);
    try {
      await api.post("/admin/payments", { user_id: openPay.id, amount });
      toast.success("Pagamento registrado");
      setOpenPay(null); setPayAmount("");
      load();
    } catch { toast.error("Erro"); }
  };

  const activateAutopay = async (c) => {
    try {
      const { data } = await api.post(`/admin/billing/preapproval/${c.id}`);
      if (data.init_point) {
        navigator.clipboard.writeText(data.init_point);
        toast.success("Link de autopay gerado e copiado! Envie ao cliente para autorizar.");
      } else {
        toast.success("Autopay criado");
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const cancelAutopay = async (c) => {
    if (!window.confirm(`Cancelar autopay de ${c.name}?`)) return;
    try {
      await api.delete(`/admin/billing/preapproval/${c.id}`);
      toast.success("Autopay cancelado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-semibold">Base de clientes</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Clientes</h1>
        </div>
        <Button onClick={openCreate} data-testid="client-create-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus weight="bold" className="mr-2" size={16} /> Novo cliente
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 items-center mb-4">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            {FILTERS.map((f) => (
              <TabsTrigger key={f.key} value={f.key} data-testid={`filter-${f.key}`}>{f.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16}/>
          <Input
            placeholder="Buscar por nome, email, usuário..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10"
            data-testid="client-search-input"
          />
        </div>
      </div>

      <Card className="border-zinc-200 p-0 overflow-hidden card-soft hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/80 border-b border-zinc-200">
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500">
                <th className="px-5 py-3 font-semibold">Cliente</th>
                <th className="px-3 py-3 font-semibold">Plano</th>
                <th className="px-3 py-3 font-semibold">Valor</th>
                <th className="px-3 py-3 font-semibold">Vencimento</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-3 py-3 font-semibold">Fidelidade</th>
                <th className="px-3 py-3 font-semibold text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-zinc-500">Nenhum cliente encontrado.</td></tr>
              )}
              {filtered.map((c) => {
                const plan = plans.find((p) => p.id === c.plan_id);
                return (
                  <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`client-row-${c.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium">{c.name}</div>
                      <div className="text-zinc-500 text-xs">{c.email} · @{c.username}</div>
                    </td>
                    <td className="px-3 py-3 text-zinc-700">{plan ? plan.name : "—"}</td>
                    <td className="px-3 py-3 tab-nums">{formatBRL(c.plan_value)}</td>
                    <td className="px-3 py-3 tab-nums">{formatDate(c.current_due_date)}</td>
                    <td className="px-3 py-3"><StatusBadge status={c.status === "ativo" ? c.payment_status : c.status} /></td>
                    <td className="px-3 py-3"><LoyaltyBadge tier={c.loyalty_tier} size="sm" /></td>
                    <td className="px-3 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`client-actions-${c.id}`}><DotsThreeVertical size={20} weight="bold"/></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onClick={() => openEdit(c)}><PencilSimple size={16} className="mr-2"/> Editar</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setSubsOpen(c)} data-testid={`client-subs-${c.id}`}>
                            <Users size={16} className="mr-2"/> Subcontas / Subplanos
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => showAudit(c)}><ClockCounterClockwise size={16} className="mr-2"/> Histórico de alterações</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setOpenPay(c); setPayAmount(String(c.plan_value || "")); }}>
                            <CurrencyDollar size={16} className="mr-2"/> Registrar pagamento
                          </DropdownMenuItem>
                          {c.auto_renew ? (
                            <DropdownMenuItem onClick={() => cancelAutopay(c)}>
                              <Lightning size={16} className="mr-2 text-rose-600" weight="fill"/> Cancelar autopay
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => activateAutopay(c)}>
                              <Lightning size={16} className="mr-2 text-blue-600"/> Ativar autopay (Mercado Pago)
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {c.status === "ativo" ? (
                            <>
                              <DropdownMenuItem onClick={() => setStatus(c, "bloqueado")}><Lock size={16} className="mr-2"/> Bloquear</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setStatus(c, "inativo")}><ArrowClockwise size={16} className="mr-2"/> Inativar</DropdownMenuItem>
                            </>
                          ) : (
                            <DropdownMenuItem onClick={() => setStatus(c, "ativo")}><LockOpen size={16} className="mr-2"/> Reativar</DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => remove(c)} className="text-rose-600"><Trash size={16} className="mr-2"/> Excluir</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2" data-testid="clients-mobile-list">
        {filtered.length === 0 && (
          <Card className="border-zinc-200 p-8 text-center text-zinc-500 text-sm">Nenhum cliente encontrado.</Card>
        )}
        {filtered.map((c) => {
          const plan = plans.find((p) => p.id === c.plan_id);
          return (
            <Card key={c.id} className="border-zinc-200 p-4 card-soft" data-testid={`client-mcard-${c.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold truncate">{c.name}</div>
                  <div className="text-xs text-zinc-500 truncate">@{c.username} · {plan?.name || "—"}</div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="-mr-2" data-testid={`client-mactions-${c.id}`}>
                      <DotsThreeVertical size={20} weight="bold"/>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem onClick={() => openEdit(c)}><PencilSimple size={16} className="mr-2"/> Editar</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setSubsOpen(c)} data-testid={`client-subs-m-${c.id}`}>
                      <Users size={16} className="mr-2"/> Subcontas
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => showAudit(c)}><ClockCounterClockwise size={16} className="mr-2"/> Histórico</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setOpenPay(c); setPayAmount(String(c.plan_value || "")); }}>
                      <CurrencyDollar size={16} className="mr-2"/> Registrar pagamento
                    </DropdownMenuItem>
                    {c.auto_renew ? (
                      <DropdownMenuItem onClick={() => cancelAutopay(c)}>
                        <Lightning size={16} className="mr-2 text-rose-600" weight="fill"/> Cancelar autopay
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => activateAutopay(c)}>
                        <Lightning size={16} className="mr-2 text-blue-600"/> Ativar autopay
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    {c.status === "ativo" ? (
                      <>
                        <DropdownMenuItem onClick={() => setStatus(c, "bloqueado")}><Lock size={16} className="mr-2"/> Bloquear</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatus(c, "inativo")}><ArrowClockwise size={16} className="mr-2"/> Inativar</DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem onClick={() => setStatus(c, "ativo")}><LockOpen size={16} className="mr-2"/> Reativar</DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => remove(c)} className="text-rose-600"><Trash size={16} className="mr-2"/> Excluir</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-zinc-500">Valor</div>
                  <div className="tab-nums font-medium">{formatBRL(c.plan_value)}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-zinc-500">Vencimento</div>
                  <div className="tab-nums font-medium">{formatDate(c.current_due_date)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-100">
                <StatusBadge status={c.status === "ativo" ? c.payment_status : c.status} />
                <LoyaltyBadge tier={c.loyalty_tier} size="sm" />
              </div>
            </Card>
          );
        })}
      </div>

      {/* Form Dialog */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar cliente" : "Novo cliente"}</DialogTitle>
          </DialogHeader>

          {generated ? (
            <div className="space-y-4 py-4">
              <Card className="border-emerald-200 bg-emerald-50/50 p-4">
                <div className="text-sm text-emerald-800 font-medium mb-2">Cliente criado com sucesso!</div>
                <div className="text-sm text-zinc-700">
                  Compartilhe estas credenciais com o cliente:
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <CredField label="Usuário" value={generated.username} />
                  <CredField label="Senha" value={generated.password} />
                </div>
              </Card>
              <DialogFooter>
                <Button onClick={() => { setOpenForm(false); setGenerated(null); }} data-testid="client-cred-close">Fechar</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4 py-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Nome completo" required>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="client-form-name"/>
                </Field>
                <Field label="E-mail" required>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="client-form-email"/>
                </Field>
                <Field label="Usuário (login)" required>
                  <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} disabled={!!editing} required data-testid="client-form-username"/>
                </Field>
                <Field label={editing ? "Nova senha (opcional)" : "Senha (deixe vazio para gerar)"}>
                  <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="client-form-password"/>
                </Field>
                <Field label="Telefone / WhatsApp">
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+55 11 9 9999-9999" data-testid="client-form-phone"/>
                </Field>
                <Field label="Plano">
                  <Select value={form.plan_id || "none"} onValueChange={(v) => {
                    const p = plans.find((x) => x.id === v);
                    setForm({ ...form, plan_id: v === "none" ? "" : v, plan_value: p ? p.base_value : form.plan_value, recurrence: p ? p.recurrence : form.recurrence });
                  }}>
                    <SelectTrigger data-testid="client-form-plan"><SelectValue placeholder="Sem plano"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Sem plano —</SelectItem>
                      {plans.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name} · {formatBRL(p.base_value)}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Valor (R$)">
                  <Input type="number" step="0.01" value={form.plan_value} onChange={(e) => setForm({ ...form, plan_value: e.target.value })} data-testid="client-form-value"/>
                </Field>
                <Field label="Recorrência">
                  <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                    <SelectTrigger data-testid="client-form-recurrence"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensal">Mensal</SelectItem>
                      <SelectItem value="trimestral">Trimestral</SelectItem>
                      <SelectItem value="semestral">Semestral</SelectItem>
                      <SelectItem value="anual">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Dia do vencimento (1-28)">
                  <Input type="number" min="1" max="28" value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} data-testid="client-form-dueday"/>
                </Field>
                {editing && (
                  <Field label="Próximo vencimento (opcional — sobrescreve)">
                    <Input
                      type="date"
                      value={form.current_due_date ? form.current_due_date.slice(0, 10) : ""}
                      onChange={(e) => setForm({ ...form, current_due_date: e.target.value })}
                      data-testid="client-form-current-due"
                    />
                  </Field>
                )}
                <Field label="Desconto manual (%)">
                  <Input type="number" min="0" max="100" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} data-testid="client-form-discount"/>
                </Field>
                <Field label="Nível fidelidade (manual)">
                  <Select value={form.custom_loyalty || "auto"} onValueChange={(v) => setForm({ ...form, custom_loyalty: v === "auto" ? "" : v })}>
                    <SelectTrigger data-testid="client-form-loyalty"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Automático</SelectItem>
                      <SelectItem value="bronze">Bronze</SelectItem>
                      <SelectItem value="silver">Silver</SelectItem>
                      <SelectItem value="gold">Gold</SelectItem>
                      <SelectItem value="king">King</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpenForm(false)}>Cancelar</Button>
                <Button type="submit" data-testid="client-form-submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                  {editing ? "Salvar alterações" : "Cadastrar"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={!!openPay} onOpenChange={(v) => !v && setOpenPay(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar pagamento</DialogTitle></DialogHeader>
          {openPay && (
            <div className="space-y-3 py-2">
              <div className="text-sm">
                <div className="text-zinc-500 text-xs uppercase tracking-widest">Cliente</div>
                <div className="font-medium">{openPay.name}</div>
              </div>
              <Field label="Valor (R$)">
                <Input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="payment-amount-input"/>
              </Field>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenPay(null)}>Cancelar</Button>
                <Button onClick={registerPay} data-testid="payment-confirm-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">Confirmar pagamento</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Audit log dialog */}
      <Dialog open={!!auditOpen} onOpenChange={(v) => !v && setAuditOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClockCounterClockwise size={20} weight="duotone" className="text-blue-600"/>
              Histórico de alterações — {auditOpen?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {auditLoading ? (
              <div className="text-center py-8 text-zinc-500">Carregando...</div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 text-sm">
                Nenhuma alteração registrada ainda. As mudanças em <strong>dia do vencimento</strong>, <strong>recorrência</strong> ou <strong>data de vencimento</strong> ficam salvas aqui.
              </div>
            ) : (
              <ul className="space-y-3" data-testid="audit-log-list">
                {auditLogs.map((log) => (
                  <li key={log.id} className="border border-zinc-200 rounded-lg p-3 bg-white" data-testid={`audit-log-${log.id}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                      <div className="text-sm">
                        <span className="font-semibold">{log.admin_name || "Admin"}</span>
                        <span className="text-zinc-500"> alterou</span>
                        {log.manual_override && (
                          <span className="ml-2 text-[10px] tracking-widest uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                            Override manual
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500">{formatDate(log.created_at)} · {new Date(log.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                    <div className="space-y-1.5">
                      {log.changes.map((ch, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-1.5 text-xs">
                          <span className="text-[10px] tracking-widest uppercase text-zinc-500 font-semibold">
                            {ch.field === "due_day" ? "Dia do venc." : ch.field === "recurrence" ? "Recorrência" : "Data venc."}
                          </span>
                          <span className="text-zinc-500 line-through">{ch.from ?? "—"}</span>
                          <span className="text-zinc-400">→</span>
                          <span className="text-zinc-900 font-semibold tab-nums">{ch.to ?? "—"}</span>
                        </div>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditOpen(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subaccounts dialog */}
      <SubaccountsDialog
        owner={subsOpen}
        open={!!subsOpen}
        onOpenChange={(v) => !v && setSubsOpen(null)}
        onChanged={() => load()}
      />
    </div>
  );
};

const Field = ({ label, children, required }) => (
  <div>
    <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">{label}{required && <span className="text-rose-500"> *</span>}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const CredField = ({ label, value }) => {
  const copy = () => { navigator.clipboard.writeText(value); toast.success(`${label} copiado`); };
  return (
    <div className="border border-zinc-200 rounded-lg p-3 bg-white">
      <div className="text-[10px] tracking-widest uppercase text-zinc-500">{label}</div>
      <div className="flex items-center justify-between mt-1">
        <code className="font-mono text-sm">{value}</code>
        <button onClick={copy} className="text-zinc-500 hover:text-blue-600" title="Copiar"><ClipboardText size={16}/></button>
      </div>
    </div>
  );
};

export default Clients;
